<template>
  <v-dialog v-model="showDialog" width="500" persistent>
    <v-card>
      <v-card-title class="flex items-center">
        <span>{{ t("beneficiaries.addTitle") }}</span>
        <v-spacer />
        <v-btn
          icon="mdi-close"
          :aria-label="t('common.close')"
          variant="text"
          size="small"
          :disabled="validating || creating"
          @click="closeDialog()"
        ></v-btn>
      </v-card-title>

      <template v-if="step === 'form'">
        <v-card-text>
          <v-form
            id="add-beneficiary-form"
            ref="form"
            :disabled="validating"
            @submit.prevent="validateBeneficiary"
          >
            <v-text-field
              v-model.trim="name"
              :rules="[required, maxLength(70), validBeneficiaryName]"
              class="mb-2"
              counter="70"
              maxlength="70"
            >
              <template #label>
                {{ t("common.name") }}
                <span class="text-error">*</span>
              </template>
            </v-text-field>
            <v-text-field v-model.trim="iban" :rules="[required, validIban]" class="mb-2">
              <template #label>
                {{ t("common.iban") }}
                <span class="text-error">*</span>
              </template>
            </v-text-field>
            <v-text-field v-model.trim="bic" :rules="[required, validBic]">
              <template #label>
                {{ t("beneficiaries.bic") }}
                <span class="text-error">*</span>
              </template>
            </v-text-field>
          </v-form>
        </v-card-text>

        <v-divider />

        <v-card-actions class="px-4 py-3">
          <v-btn :disabled="validating" @click="closeDialog()">{{ t("common.cancel") }}</v-btn>
          <v-spacer />
          <v-btn
            variant="flat"
            type="submit"
            form="add-beneficiary-form"
            :loading="validating"
            :disabled="validating"
            color="primary"
            >{{ t("beneficiaries.validate") }}</v-btn
          >
        </v-card-actions>
      </template>

      <template v-else-if="validation">
        <v-card-text>
          <div class="mb-4">
            <div class="text-sm text-medium-emphasis">{{ t("common.name") }}</div>
            <div>{{ name }}</div>
            <div class="text-sm text-medium-emphasis mt-2">{{ t("common.iban") }}</div>
            <div>{{ normalizedIban }}</div>
            <div class="text-sm text-medium-emphasis mt-2">{{ t("beneficiaries.bic") }}</div>
            <div>{{ normalizedBic }}</div>
          </div>

          <div class="text-sm text-medium-emphasis mb-2">
            {{ t("transactions.details.verificationOfPayee.title") }}
          </div>
          <v-chip
            :prepend-icon="validationIcon"
            :color="validationColor"
            variant="tonal"
            class="mb-3"
          >
            {{ validationLabel }}
          </v-chip>
          <div class="text-sm text-medium-emphasis">{{ validationMessage }}</div>
        </v-card-text>

        <v-divider />

        <v-card-actions class="px-4 py-3">
          <v-btn :disabled="creating" @click="backToForm()">{{ t("beneficiaries.back") }}</v-btn>
          <v-spacer />
          <v-btn
            variant="flat"
            :loading="creating"
            :disabled="creating"
            color="primary"
            @click="createBeneficiary()"
            >{{ t("common.add") }}</v-btn
          >
        </v-card-actions>
      </template>
    </v-card>
  </v-dialog>
</template>

<script lang="ts" setup>
import { useI18n } from "@/composables/useI18n";
import { useRules } from "@/composables/useRules";
import { call } from "@/firebase";
import { useToast } from "@/store";
import { startAuthentication } from "@/utils/webAuthn";
import type { BeneficiaryValidation } from "@aircap/common/treezor";
import { captureException } from "@sentry/vue";
import { computed, ref, watch } from "vue";
import type { VForm } from "vuetify/components";

const props = defineProps<{
  modelValue: boolean;
  accountId: string;
}>();

const emit = defineEmits<{
  "update:modelValue": [value: boolean];
}>();

const { t } = useI18n();
const { required, maxLength } = useRules();
const { showSuccessToast, showErrorToast } = useToast();

const form = ref<VForm>();
const name = ref("");
const iban = ref("");
const bic = ref("");
const step = ref<"form" | "validation">("form");
const validation = ref<BeneficiaryValidation>();
const validating = ref(false);
const creating = ref(false);

const normalizedIban = computed(() => iban.value.replace(/\s/g, "").toUpperCase());
const normalizedBic = computed(() => bic.value.replace(/\s/g, "").toUpperCase());

/** Treezor SCT beneficiary name charset (see createBeneficiary). */
const validBeneficiaryName = (value: unknown) => {
  if (!value) return true;
  if (typeof value !== "string") return t("errors.invalidBeneficiaryName");
  return /^[A-Za-z0-9/\-?:().,'+ ]+$/.test(value) || t("errors.invalidBeneficiaryName");
};

const validIban = (value: unknown) => {
  if (!value) return true;
  if (typeof value !== "string") return t("errors.invalidIban");
  return (
    /^[A-Z]{2}\d{2}[A-Z0-9]{11,30}$/.test(value.replace(/\s/g, "").toUpperCase()) ||
    t("errors.invalidIban")
  );
};

const validBic = (value: unknown) => {
  if (!value) return true;
  if (typeof value !== "string") return t("errors.invalidBic");
  return (
    /^[A-Z]{4}[A-Z]{2}[A-Z0-9]{2}([A-Z0-9]{3})?$/.test(value.replace(/\s/g, "").toUpperCase()) ||
    t("errors.invalidBic")
  );
};

const showDialog = computed({
  get: () => props.modelValue,
  set: (value) => emit("update:modelValue", value),
});

const validationLabel = computed(() => {
  const result = validation.value?.vop.result;
  if (!result) return "";
  switch (result) {
    case "MTCH":
      return t("transactions.details.verificationOfPayee.Match");
    case "CMTC":
      return t("transactions.details.verificationOfPayee.CloseMatch");
    case "NMTC":
      return t("transactions.details.verificationOfPayee.NoMatch");
    case "NOAP":
      return t("transactions.details.verificationOfPayee.VerificationNotPossible");
  }
});

const validationColor = computed(() => {
  const result = validation.value?.vop.result;
  if (!result) return "info";
  switch (result) {
    case "MTCH":
      return "success";
    case "CMTC":
      return "warning";
    case "NMTC":
      return "error";
    case "NOAP":
      return "warning";
  }
});

const validationIcon = computed(() => {
  const result = validation.value?.vop.result;
  if (!result) return "mdi-shield-alert";
  switch (result) {
    case "MTCH":
      return "mdi-shield-check";
    case "CMTC":
      return "mdi-shield-alert";
    case "NMTC":
      return "mdi-alert-circle";
    case "NOAP":
      return "mdi-shield-alert";
  }
});

const validationMessage = computed(() => {
  const vop = validation.value?.vop;
  if (!vop) return "";
  switch (vop.result) {
    case "MTCH":
      return t("beneficiaries.validation.match");
    case "CMTC":
      return t("beneficiaries.validation.closeMatch", {
        matchedName: vop.matchedName || t("common.none"),
      });
    case "NMTC":
      return t("beneficiaries.validation.noMatch");
    case "NOAP":
      return vop.reasonCode === "9009"
        ? t("beneficiaries.validation.unavailable")
        : t("beneficiaries.validation.notPossible");
  }
});

watch(
  () => props.modelValue,
  (modelValue) => {
    if (modelValue) {
      name.value = "";
      iban.value = "";
      bic.value = "";
      step.value = "form";
      validation.value = undefined;
      form.value?.resetValidation();
    }
  },
);

const closeDialog = () => {
  showDialog.value = false;
};

const backToForm = () => {
  step.value = "form";
  validation.value = undefined;
};

const validateBeneficiary = async () => {
  if (!form.value) return;
  const { valid } = await form.value.validate();
  if (!valid) return;

  validating.value = true;
  try {
    validation.value = await call("validateTreezorBeneficiary")({
      name: name.value,
      iban: normalizedIban.value,
    });
    step.value = "validation";
  } catch (cause) {
    const error = new Error("Error validating beneficiary", { cause });
    console.error(error);
    captureException(error);
    showErrorToast(cause instanceof Error ? cause.message : undefined);
  } finally {
    validating.value = false;
  }
};

const createBeneficiary = async () => {
  if (!validation.value) return;

  creating.value = true;
  try {
    const beneficiary = {
      name: name.value,
      bic: normalizedBic.value,
      iban: normalizedIban.value,
    };
    const { webAuthnAuthenticationOptions } = await call("createTreezorBeneficiarySCAPayload")({
      accountId: props.accountId,
      ...beneficiary,
    });
    const webAuthnAuthenticationResponse = await startAuthentication({
      optionsJSON: webAuthnAuthenticationOptions,
    });
    await call("createTreezorBeneficiary")({
      accountId: props.accountId,
      ...beneficiary,
      webAuthnAuthenticationResponse,
    });
    showSuccessToast();
    closeDialog();
  } catch (cause) {
    const error = new Error("Error creating beneficiary", { cause });
    console.error(error);
    captureException(error);
    showErrorToast(cause instanceof Error ? cause.message : undefined);
  } finally {
    creating.value = false;
  }
};
</script>
